/* eslint-disable sonarjs/cognitive-complexity */
import { PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { WidgetSlot, WidgetSlotConfiguration, WidgetType } from '@/api/rest/resources/types/widget';
import { ChartLegendBarItem } from '@/components/Charts/components/types';
import { useBenchmarkSelection } from '@/hooks/useBenchmarkSelection';
import { useGetIndicatorProductSubscriptionConfigurations } from '@/hooks/useDashboardIndicatorProductSubscriptionConfiguration';
import { APPROVED_DEFAULT_FILTERS } from '@/hooks/useInSituIndicatorConfigurationWidgets';
import { useIsStaffUser } from '@/hooks/useIsStaffUser';
import { usePlotFactsBatchRequest } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray, getFactsFromFactArray } from '@/pages/shared/hooks/utils';
import { isNullish } from '@/utils/booleans';
import { extractFactMappingValue, FactMapping, getFactMappingValues } from '@/utils/factMapping';
import { printYear } from '@/utils/formatting/date';

type PlotCardProps = {
  facts: {
    name: string;
    dateFilters?: { start_date_inclusive?: string; end_date_inclusive?: string };
    hasCustomBenchmark?: boolean;
  }[];
  widgetConfiguration: WidgetSlotConfiguration;
  indicatorProductSubscriptionConfigId?: string;
};

export const usePlotCardWidget = ({
  facts,
  widgetConfiguration,
  indicatorProductSubscriptionConfigId,
}: PlotCardProps): WidgetSlotConfiguration | null => {
  const isStaffUser = useIsStaffUser();

  const customBenchmarkFact = facts.find((fact) => fact.hasCustomBenchmark)?.name ?? '';
  const [selectedBenchmarkId, setSelectedBenchmarkId] = useBenchmarkSelection(customBenchmarkFact);
  const customBenchmark = customBenchmarkFact
    ? {
        selectedBenchmark: selectedBenchmarkId,
        setSelectedBenchmark: setSelectedBenchmarkId,
        showCustomBenchmark: true,
        indicator: `${customBenchmarkFact}_plot_card`, // add plot_card to differentiate custom benchmark from org card benchmarks
      }
    : undefined;

  const ipSubConfigurations = useGetIndicatorProductSubscriptionConfigurations(
    undefined,
    true,
    isStaffUser ? undefined : APPROVED_DEFAULT_FILTERS,
  );
  const sourceRef = indicatorProductSubscriptionConfigId
    ? ipSubConfigurations.data?.results.find((config) => config.id === indicatorProductSubscriptionConfigId)
        ?.file_info?.[0]?.id
    : undefined;

  const factResults = usePlotFactsBatchRequest(facts, undefined, sourceRef);

  if (!factResults || factResults.length === 0) {
    return null;
  }

  return addRecurivelyFactValuesToWidgets({
    widgetConfiguration,
    facts: factResults,
    sourceRef,
    customBenchmark,
  }) as WidgetSlotConfiguration;
};

const TICK_FORMATTERS: Record<string, (value: string) => string> = {
  printYear,
};

const addRecurivelyFactValuesToWidgets = ({
  widgetConfiguration,
  facts,
  sourceRef,
  customBenchmark,
}: {
  widgetConfiguration: WidgetSlotConfiguration | WidgetSlot;
  facts: PlotReportFactElement[];
  sourceRef?: string;
  customBenchmark?: {
    selectedBenchmark: string | null;
    setSelectedBenchmark: (newBenchmarkId: string | null) => void;
    showCustomBenchmark: boolean;
    indicator: string;
  };
}): WidgetSlotConfiguration | WidgetSlot => {
  if (!widgetConfiguration.widgets) {
    return { ...widgetConfiguration, relatedData: { ...(widgetConfiguration as WidgetSlot).relatedData, sourceRef } };
  }

  return {
    ...widgetConfiguration,
    widgets: widgetConfiguration.widgets.map((widget) => {
      let updatedWidget = widget;

      if (Array.isArray(updatedWidget.relatedData?.factMappings)) {
        const mappedValues: Record<string, unknown> = {};
        const isGraph = updatedWidget?.relatedData?.isGraph;

        updatedWidget.relatedData.factMappings.forEach((mapping: FactMapping) => {
          if (isGraph) {
            const factRetriver = mapping.isSingleFact ? getFactFromFactArray : getFactsFromFactArray;
            const allFacts = factRetriver(facts, mapping.factName, mapping.factLabels);
            const factValues = Array.isArray(allFacts) ? allFacts : allFacts?.value;
            mappedValues[mapping.consumerKey ?? 'fact'] = factValues ?? null;
          } else {
            const fact = getFactFromFactArray(facts, mapping.factName, mapping.factLabels);
            mappedValues[mapping.consumerKey ?? 'fact'] = fact ?? null;
          }
        });

        updatedWidget = {
          ...updatedWidget,
          relatedData: { ...updatedWidget.relatedData, ...mappedValues, sourceRef },
        };

        // Resolve referenceLines: look up benchmark facts and extract the y value
        if (Array.isArray(updatedWidget.relatedData?.referenceLines)) {
          const resolvedLines = getFactMappingValues({
            facts,
            factMappings: updatedWidget.relatedData.referenceLines,
            outputKeys: ['label', 'dataKey'],
          });

          updatedWidget = {
            ...updatedWidget,
            relatedData: { ...updatedWidget.relatedData, referenceLines: resolvedLines, sourceRef },
          };
        }

        // Resolve thresholds: look up facts and build stable horizontal threshold lines
        if (Array.isArray(updatedWidget.relatedData?.thresholds?.factMappings)) {
          const resolvedThresholds = getFactMappingValues({
            facts,
            factMappings: updatedWidget.relatedData.thresholds.factMappings,
            outputKeys: ['label'],
          });

          updatedWidget = {
            ...updatedWidget,
            relatedData: { ...updatedWidget.relatedData, thresholds: resolvedThresholds, sourceRef },
          };
        }

        // Resolve y axis min/max values from fact mappings
        if (Array.isArray(updatedWidget.relatedData?.yAxisProps?.factMappings)) {
          const resolvedDomain = getFactMappingValues({
            facts,
            factMappings: updatedWidget.relatedData.yAxisProps.factMappings,
            outputKeys: [],
          }).flatMap((item) => (isNullish(item?.value) ? [] : [item?.value]));

          updatedWidget = {
            ...updatedWidget,
            relatedData: {
              ...updatedWidget.relatedData,
              sourceRef,
              yAxisProps: { ...updatedWidget.relatedData.yAxisProps, domain: resolvedDomain },
            },
          };
        }

        const tickFormatterRef = updatedWidget.relatedData?.xAxisProps?.tickFormatter;
        if (typeof tickFormatterRef === 'string' && TICK_FORMATTERS[`${tickFormatterRef}`]) {
          updatedWidget = {
            ...updatedWidget,
            relatedData: {
              ...updatedWidget.relatedData,
              sourceRef,
              xAxisProps: {
                ...updatedWidget.relatedData.xAxisProps,
                tickFormatter: TICK_FORMATTERS[`${tickFormatterRef}`],
              },
            },
          };
        }

        // Resolve legendItems
        if (Array.isArray(updatedWidget.relatedData?.legendItems)) {
          const resolvedLegendItems = updatedWidget.relatedData.legendItems.filter(
            (item: ChartLegendBarItem & { factMapping?: FactMapping }) => {
              if (!item.factMapping) {
                return true;
              }
              const factValue = extractFactMappingValue({ facts, factMapping: item.factMapping });
              return !isNullish(factValue);
            },
          );

          updatedWidget = {
            ...updatedWidget,
            relatedData: { ...updatedWidget.relatedData, legendItems: resolvedLegendItems, sourceRef },
          };
        }
      }

      if (widget.type === WidgetType.a_chart_heading && customBenchmark) {
        updatedWidget = {
          ...updatedWidget,
          relatedData: {
            ...updatedWidget.relatedData,
            ...customBenchmark,
          },
        };
      }

      if (widget.type === WidgetType.o_data_processor && customBenchmark) {
        updatedWidget = {
          ...updatedWidget,
          relatedData: {
            ...updatedWidget.relatedData,
            selectedBenchmark: customBenchmark.selectedBenchmark,
            customBenchmarkFact: customBenchmark.indicator,
          },
        };
      }

      if (updatedWidget.widgets) {
        updatedWidget = addRecurivelyFactValuesToWidgets({
          widgetConfiguration: updatedWidget,
          facts,
          customBenchmark,
        }) as WidgetSlot;
      }

      return updatedWidget;
    }),
  };
};
